import * as R from '../../Router/routes';
import { AsyncLoanConfirm, AsyncNewsConsent } from '../../Router/Router';
import { BundleBadge } from 'components/Bundle/BundleBadge/BundleBadge';
import {
  Button,
  Card,
  Heading,
  Loader,
  Paragraph,
  Placeholder,
  Slider,
  Swiper,
} from '../../components';
import { dateFormat, numberToCurrency } from 'utils/translate';
import { getBackUrlMenuScreens } from 'utils/getUrl';
import { getClientProductNumber } from 'utils/getProductNumber';
import { getConstraintsAmountValue } from 'utils/defaultValues';
import { Helmet } from 'react-helmet-async';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  useApplication,
  useClient,
  useClientConstraints,
  useClientOffer,
  useClientSettings,
  useDiscounts,
} from '../../hooks/webapi';
import { useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import InfoIcon from 'assets/icons/info.svg?react';
import styles from './AdditionalAmount.module.scss';
import Tracked from '../../customAnalytics/components/Tracked';
import useBreakpoint from '../../hooks/useBreakpoint';
import useBundle from 'hooks/useBundle';
import useLatestLoan from '../../hooks/webapi/useLatestLoan';
import useTracking from '../../customAnalytics/hooks/useTracking';
import WarningExclamationIcon from 'assets/icons/bundle/warning-exclamation.svg?react';

function preloadConfirmLoan() {
  AsyncLoanConfirm.preload();
  AsyncNewsConsent.preload();
}

type WarningProps = {
  text: string;
  className?: string;
};

const WarningAdditionalAmount = ({ text, className }: WarningProps) => (
  <div
    className={classes(styles.warning, className)}
    data-test="additional-amount-warning"
  >
    <WarningExclamationIcon data-test="additional-amount-warning-icon" />
    <span
      dangerouslySetInnerHTML={{
        __html: text,
      }}
      data-test="additional-amount-warning-text"
    ></span>
  </div>
);

export default memo(function AdditionalAmount() {
  const [amount, setAmount] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOffer, setIsLoadingOffer] = useState(true);
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);

  const { size } = useBreakpoint();
  const { client } = useClient();
  const { latestLoan } = useLatestLoan();
  const { clientConstraints, fetchClientConstraints } = useClientConstraints();
  const { clientOffers = [], fetchClientOffer } = useClientOffer();
  const { fetchApplication, createApplication } = useApplication();
  const { discounts, fetchDiscounts, largestInterestDiscount } = useDiscounts();
  const { clientSettings } = useClientSettings();
  const navigate = useNavigate();
  const { handleTrack } = useTracking();
  const { t } = useTranslation('additional_amount');
  const { isBundleAccepted, isBundleAvailable, bundleDays } = useBundle();
  const isWarningBannerVisible = !isBundleAccepted && isBundleAvailable;

  const clientProduct = getClientProductNumber(client?.availableProducts);

  useEffect(() => {
    if (size.index <= 2) {
      preloadConfirmLoan();
    }
  }, []);

  const handleAnalytics = useCallback(
    (amount: number) => {
      handleTrack({
        eventType: 'change',
        value: amount,
        description: 'Additional amount slider',
      });
    },
    [handleTrack],
  );

  useEffect(() => {
    (async () => {
      try {
        const [fetchedClientConstraints] = await Promise.all([
          !clientConstraints && fetchClientConstraints(clientProduct),
          !discounts && fetchDiscounts(),
        ]);

        const constraintsData = clientConstraints || fetchedClientConstraints;

        const constraintsAmountDefaultValue =
          constraintsData && getConstraintsAmountValue(constraintsData);

        if (
          !clientConstraints &&
          constraintsData &&
          constraintsAmountDefaultValue
        ) {
          await fetchClientOffer(
            clientProduct,
            constraintsAmountDefaultValue,
            constraintsData.termInterval.defaultValue,
          );

          setAmount(getConstraintsAmountValue(constraintsData));

          // Analytics
          handleTrack({
            eventType: 'fetch',
            description: 'Fetched default amount slider value',
            value: constraintsAmountDefaultValue,
          });
        } else if (constraintsData) {
          setAmount(getConstraintsAmountValue(constraintsData));
        }
      } catch {
      } finally {
        setIsLoadingOffer(false);
        setIsLoading(false);
      }
    })();
  }, [clientProduct, fetchClientConstraints, fetchClientOffer, fetchDiscounts, navigate, handleTrack]);

  const handleAmountChange = useCallback(
    async (amount: number) => {
      handleAnalytics(amount);
      try {
        setIsLoadingOffer(true);
        if (clientConstraints) {
          setAmount(amount);
          await fetchClientOffer(
            clientProduct,
            amount,
            clientConstraints?.termInterval.defaultValue,
          );
        }
      } catch {
      } finally {
        setIsLoadingOffer(false);
      }
    },
    [clientProduct, clientConstraints, fetchClientOffer, handleAnalytics],
  );

  const postApplicationCreated = async () => {
    !clientSettings?.acceptNews
      ? navigate(R.NEWS_CONSENT, {
          state: { nextUrl: R.ADDITIONAL_AMOUNT_CONFIRM },
        })
      : navigate(R.ADDITIONAL_AMOUNT_CONFIRM, {
          state: { backUrl: R.ADDITIONAL_AMOUNT },
        });
  };

  const apply = useCallback(async () => {
    try {
      setIsCreatingApplication(true);
      if (clientConstraints?.termInterval.defaultValue && amount) {
        await createApplication({
          body: {
            amount,
            productNumber: client?.productNumber,
            term: clientConstraints.termInterval.defaultValue,
          },
          postApplicationCreated,
        });
        await fetchApplication();
      }
    } catch {
      setIsCreatingApplication(false);
    }
  }, [
    clientConstraints?.termInterval.defaultValue,
    amount,
    createApplication,
    clientSettings?.acceptNews,
    fetchApplication,
    navigate,
  ]);

  const availableDiscount = largestInterestDiscount?.discountPercent;

  const maximumDiscount =
    largestInterestDiscount?.amountRanges[0].max &&
    clientConstraints?.amountInterval.max &&
    largestInterestDiscount?.amountRanges[0].max <=
      clientConstraints?.amountInterval.max
      ? largestInterestDiscount?.amountRanges[0].max
      : clientConstraints?.amountInterval.max;

  const discountBanner =
    availableDiscount && clientOffers[0]?.newInterest
      ? {
          discount: largestInterestDiscount?.discountPercent,
          dueDate: largestInterestDiscount?.validityRanges[0]?.max,
          minValue: largestInterestDiscount?.amountRanges[0].min,
          maxValue: maximumDiscount,
        }
      : null;

  const warningBannerText = t('warning', {
    date: bundleDays?.paymentDayAdditionalAmount,
  });

  return (
    <Dashboard
      backUrl={getBackUrlMenuScreens(client, latestLoan)}
      className={styles.container}
      mobileClassName={styles['mobile-header']}
      mobileHeading={t('mobile_header')}
      discountObject={discountBanner}
      isLoadingOffer={isLoadingOffer}
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <Heading
        className={classes(
          styles.heading,
          !isBundleAccepted && styles['heading-margin'],
        )}
        size="l"
        dataTest="additionalAmount-heading"
      >
        <Placeholder dataTest="heading" loading={isLoading}>
          {t('heading', {
            maxAmount: clientConstraints?.amountInterval.max,
          })}
        </Placeholder>
      </Heading>
      {isWarningBannerVisible && (
        <WarningAdditionalAmount
          text={warningBannerText}
          className={styles.desktop}
        />
      )}
      {isBundleAccepted && (
        <BundleBadge text={t('badge_bundle')} layoutView="desktop" />
      )}
      <Card
        className={classes(
          styles['calculator-card'],
          isWarningBannerVisible && styles['warning-banner'],
        )}
        dataTest="additionalAmount-card"
      >
        {isLoading || !clientConstraints || !amount ? (
          <Loader circleClassName={styles['loader-circles']} />
        ) : (
          <>
            <Slider
              dataTest="amount-slider"
              min={clientConstraints.amountInterval.min}
              max={clientConstraints.amountInterval.max}
              step={clientConstraints.amountInterval.step}
              value={amount}
              onAfterChange={handleAmountChange}
              units={t('common:currency')}
              heading={t('amount_heading')}
              className={styles.slider}
            />
            <Swiper
              dataTest="amount-swiper"
              min={clientConstraints.amountInterval.min}
              max={clientConstraints.amountInterval.max}
              step={clientConstraints.amountInterval.step}
              defaultValue={amount}
              onChange={handleAmountChange}
              snap={95}
              heading={t('calculator:amount_heading')}
              className={styles.swiper}
              unit={t('common:currency')}
            />
          </>
        )}
        <Paragraph className={styles.info}>
          <InfoIcon
            className={styles['info-icon']}
            data-test="additionalAmount-info-icon"
          />
          <span data-test="additionalAmount-info-value">{t('info')}</span>
        </Paragraph>
      </Card>

      <div
        className={classes(
          styles['loan-info-wrapper'],
          styles['wrapper-with-bundle'],
        )}
      >
        {isWarningBannerVisible && (
          <WarningAdditionalAmount
            text={warningBannerText}
            className={styles.mobile}
          />
        )}
        {isBundleAccepted && (
          <BundleBadge text={t('badge_bundle')} layoutView="mobile" />
        )}
        {!isWarningBannerVisible && !isBundleAccepted && (
          <div className={styles['rounded-bg-mobile']} />
        )}
        <Card
          className={classes(
            styles['loan-info'],
            isBundleAccepted && styles['bundle-loan-info'],
            isWarningBannerVisible && styles['warning-banner'],
          )}
          dataTest="additionalAmount-card-loanInfo"
        >
          <div className={classes(styles.row, styles.bold)}>
            <div
              className={styles.label}
              data-test="summary-additional-amount-text"
            >
              {t('additional_amount')}
            </div>
            <div>
              <Placeholder
                loading={isLoading}
                dataTest="summary-additional-amount"
              >
                {numberToCurrency(clientOffers[0]?.newPrincipal)}
              </Placeholder>
            </div>
          </div>

          <div className={classes(styles.row)}>
            <div
              className={styles.label}
              data-test="summary-interest-amount-text"
            >
              {t('interest')}
            </div>
            <div>
              <Placeholder
                loading={isLoading}
                dataTest="summary-interest-amount"
              >
                {numberToCurrency(clientOffers[0]?.newAmountInterest)}
              </Placeholder>
            </div>
          </div>

          <div className={classes(styles.row)}>
            <div
              className={styles.label}
              data-test="summary-total-repayable-amount-text"
            >
              {t('total_to_return')}
            </div>
            <div>
              <Placeholder
                loading={isLoading}
                dataTest="summary-total-repayable-amount"
              >
                {numberToCurrency(
                  clientOffers[0]?.newAmountWithInterestAndInitialCommission,
                )}
              </Placeholder>
            </div>
          </div>

          <div className={classes(styles.row)}>
            <div className={styles.label} data-test="summary-due-date-text">
              {t('repayment_date')}
            </div>
            <div>
              <Placeholder loading={isLoading} dataTest="summary-due-date">
                {dateFormat(clientOffers[0]?.agreementEndDate)}
              </Placeholder>
            </div>
          </div>
        </Card>

        <Tracked description="Additinal amount Apply button click">
          <Button
            pending={isCreatingApplication}
            onMouseEnter={preloadConfirmLoan}
            onClick={apply}
            buttonClassName={styles['continue-btn']}
            dataTest="continue"
          >
            {t('apply')}
          </Button>
        </Tracked>
      </div>
    </Dashboard>
  );
});
